import { Injectable } from '@angular/core';
import { RSA_PUBLIC_KEY } from '../../../environments/kays-cryptage/public-key';

export interface EncryptedEnvelope {
  encryptedKey: string;
  iv: string;
  ciphertext: string;
  authTag: string;
}

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  private rsaPublicKey: CryptoKey | null = null;

  // Importe la clé publique RSA une seule fois
  private async getPublicKey(): Promise<CryptoKey> {
    if (this.rsaPublicKey) return this.rsaPublicKey;

    const pemBody = RSA_PUBLIC_KEY.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s/g, '');
    const der = Uint8Array.from(atob(pemBody), (c) => c.charCodeAt(0));

    this.rsaPublicKey = await crypto.subtle.importKey(
      'spki',
      der.buffer,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
    return this.rsaPublicKey;
  }

  async encryptPayload(body: object): Promise<EncryptedEnvelope> {
    const publicKey = await this.getPublicKey();

    // 1. Générer une clé AES-256-GCM aléatoire
    const aesKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt']
    );

    // 2. Chiffrer le body avec AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(body));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encoded
    );

    // AES-GCM en SubtleCrypto : les 16 derniers octets sont l'authTag
    const encryptedBytes = new Uint8Array(encrypted);
    const ciphertext = encryptedBytes.slice(0, -16);
    const authTag = encryptedBytes.slice(-16);

    // 3. Chiffrer la clé AES avec RSA-OAEP
    const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);
    const encryptedKey = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawAesKey
    );

    return {
      encryptedKey: this.toBase64(new Uint8Array(encryptedKey)),
      iv: this.toBase64(iv),
      ciphertext: this.toBase64(ciphertext),
      authTag: this.toBase64(authTag),
    };
  }

  private toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
